import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { colors as themeColors } from '../../../../constants/colors'
import { MiniModal } from '../../modals/MiniModal'

interface ColorCriteriaModalProps {
  colors: string[]
  labels?: string[]
  selectedIds: number[]
  isOpen: boolean
  onClose: () => void
  onApply: (ids: number[]) => void
  title: string
}

export const ColorCriteriaModal = ({
  colors,
  labels,
  selectedIds,
  isOpen,
  onClose,
  onApply,
  title,
}: ColorCriteriaModalProps) => {
  const [draftIds, setDraftIds] = useState(selectedIds)
  const hasVisibleLabel = (label?: string) => Boolean(label && !/^#|^(rgb|hsl)a?\(/i.test(label))

  useEffect(() => {
    if (isOpen) setDraftIds(selectedIds)
  }, [isOpen, selectedIds])

  const toggle = (id: number) =>
    setDraftIds(previous =>
      previous.includes(id) ? previous.filter(item => item !== id) : [...previous, id]
    )

  return (
    <Modal
      viewModal={isOpen}
      hideCloseButton
      setViewModal={open => {
        if (!open) onClose()
      }}
    >
      <Content role="dialog" aria-modal="true" aria-label={`${title}: все цвета`}>
        <Header>
          <Title>{title}</Title>
          <Close type="button" onClick={onClose} aria-label="Закрыть">
            <img src="/images/buttons/filter-modal-close.svg" alt="" width={32} height={32} />
          </Close>
        </Header>
        <Toolbar>
          <Action type="button" onClick={() => setDraftIds(colors.map((_, id) => id))}>
            Все
          </Action>
          <Action type="button" onClick={() => setDraftIds([])}>
            Сбросить
          </Action>
        </Toolbar>
        <ColorGrid>
          {colors.map((color, id) => (
            <Choice key={`${color}-${id}`} aria-label={labels?.[id] || color}>
              <Swatch $color={color} aria-hidden="true" />
              {hasVisibleLabel(labels?.[id]) && <Label>{labels?.[id]}</Label>}
              <Checkbox
                type="checkbox"
                checked={draftIds.includes(id)}
                onChange={() => toggle(id)}
                aria-label={labels?.[id] || color}
              />
            </Choice>
          ))}
        </ColorGrid>
        <Apply
          type="button"
          onClick={() => {
            onApply(draftIds)
            onClose()
          }}
        >
          Выбрать
        </Apply>
      </Content>
    </Modal>
  )
}

const Modal = styled(MiniModal)`
  width: min(579px, calc(100vw - 32px));
  height: min(568px, calc(100dvh - 32px));
  max-height: calc(100dvh - 32px);
  padding: 0;
  border-radius: 15px;
  overflow: hidden;
`

const Content = styled.div`
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  padding: 35px 36px 36px;
  display: flex;
  flex-direction: column;
  @media (max-width: 449px) {
    padding: 24px 20px;
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 30px;
`

const Title = styled.h2`
  margin: 0;
  color: ${themeColors.black};
  font-size: 24px;
  line-height: 33px;
`

const Toolbar = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
`

const Action = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  color: ${themeColors.primary};
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`

const Close = styled.button`
  display: flex;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
`

const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 140px);
  gap: 8px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  align-content: start;
  @media (max-width: 449px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

const Choice = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 140px;
  min-height: 38px;
  box-sizing: border-box;
  padding: 8px;
  border-radius: 8px;
  background: #f8f8fa;
  cursor: pointer;
  @media (max-width: 449px) {
    width: 100%;
  }
`

const Swatch = styled.span<{ $color: string }>`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`

const Checkbox = styled.input`
  appearance: none;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin: 0 0 0 auto;
  border: 0;
  background: url('/images/buttons/filter-checkbox-inactive.svg') center / 20px 20px no-repeat;
  cursor: pointer;
  &:checked {
    border-radius: 4px;
    background-color: ${themeColors.primary};
    background-image: url('/images/buttons/filter-checkbox-checkmark.svg');
  }
  &:focus-visible {
    outline: 2px solid ${themeColors.primary};
    outline-offset: 2px;
  }
`

const Label = styled.span`
  min-width: 0;
  overflow: hidden;
  color: #4b4b4b;
  font-size: 16px;
  line-height: 22px;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Apply = styled.button`
  width: 100%;
  max-width: 358px;
  min-height: 48px;
  align-self: center;
  margin-top: 24px;
  border: 0;
  border-radius: 8px;
  background: ${themeColors.primary};
  color: ${themeColors.white};
  font: inherit;
  font-weight: 700;
  cursor: pointer;
`
